/** The three shared-thread visibility levels (global-workspaces §11). Wire values, not labels. */
export type ConversationVisibility = 'private' | 'group' | 'public';

/** One level the share dialog offers, in the order it lists them. */
export interface ConversationAudienceLevel {
  visibility: ConversationVisibility;
  label: string;
  hint: string;
  icon: 'lock' | 'users' | 'globe';
}

/**
 * The levels a conversation can be moved between, widest last.
 *
 * The widest one is labelled **Workspace**, not `Public`, and its wire value is still `public`. The word
 * had to go: the same dialog now also hands out links that open to anyone on the internet, so a level
 * called `Public` meaning "everyone in the workspace" sat two rows above a link mode called
 * "Anyone with the link" meaning something far wider. One word, two reaches, one screen. `Workspace` is
 * also what the file dialog already calls the same reach, so a user who learned one does not relearn it.
 *
 * Renaming the label and keeping the value is the whole point: `public` is what the service stores and
 * what the sidebar lanes partition on, and a relabel that reached the wire would be a silent data change.
 */
export const CONVERSATION_AUDIENCE_LEVELS: ConversationAudienceLevel[] = [
  {
    visibility: 'private',
    label: 'Private',
    hint: 'Only you',
    icon: 'lock',
  },
  {
    visibility: 'group',
    label: 'Group',
    hint: 'You and the members you pick',
    icon: 'users',
  },
  {
    visibility: 'public',
    label: 'Workspace',
    hint: 'Everyone in the workspace',
    icon: 'globe',
  },
];

/** True when two id sets hold the same ids — what tells an edited group apart from an untouched one. */
export function sameMembers(
  left: ReadonlySet<string>,
  right: ReadonlySet<string>,
): boolean {
  return left.size === right.size && [...left].every((id) => right.has(id));
}
